import React, { useEffect, useState } from "react";
import { Box, Grid, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById } from "../../services/userService";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { IconBtnEditBase } from "../../common/utils/icons";
import Loader from "../../components/Molecules/Loader/Loader";
import useFetchUserRoles from "../../hooks/useFetchUserRoles";
import useFetchUserAuthTags from "../../hooks/useFetchUserAuthTags";
import useFetchUserSystems from "../../hooks/useFetchUserSystems";
import useFetchGroupsByUserId from "../../hooks/useFetchGroupsByUserId";
import AssignRole from "../../components/UserManagement/AssignRole";
import AssignTags from "../../components/UserManagement/AssignTags";
import AssignSystem from "../../components/UserManagement/AssignSystem";
import AssignGroup from "../../components/UserManagement/AssignGroup";
import { useAuth } from "../../contexts/AuthContext";

const ViewUser: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id: userId } = useParams();

  // Permission checks for user actions
  const canEditUser = hasPermission("user", "edit");
  const [formData, setFormData] = useState<any>({
    firstname: "",
    lastname: "",
    email: "",
    username: "",
    user_auth_type: "",
    is_active: true,
    roles: [],
    groups: [],
    tags: [],
    systems: [],
    created_on: "",
    updated_on: "",
    last_login: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch user assignment data
  const [userAssignedRoles] = useFetchUserRoles({
    setIsLoading,
    userId: userId || null,
  });

  const [userAssignedAuthTags] = useFetchUserAuthTags({
    setIsLoading,
    userId: userId || null,
  });

  const [userAssignedSystems] = useFetchUserSystems({
    setIsLoading,
    userId: userId || null,
  });

  // Fetch user assigned groups
  const [userAssignedGroups] = useFetchGroupsByUserId({
    setIsLoading,
    userId: userId || null,
  });

  useEffect(() => {
    if (!userId) return;

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userData = await getUserById(userId);
        if (userData) {
          setFormData({
            firstname: userData?.firstname || "",
            lastname: userData?.lastname || "",
            email: userData?.email || "",
            username: userData?.username || "",
            user_auth_type: userData?.user_auth_type || "",
            is_active: userData?.is_active,
            roles: userData?.roles || [],
            groups: userData?.groups || [],
            tags: userData?.tags || [],
            systems: userData?.systems || [],
            created_on: userData?.created_on || "",
            updated_on: userData?.updated_on || "",
            last_login: userData?.last_login || "",
          });
          setCurrentBreadcrumbPage({
            name:
              `${userData?.firstname || ""} ${
                userData?.lastname || ""
              }`.trim() || userData?.username,
            id: userData?.id,
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();

    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [userId, setCurrentBreadcrumbPage]);

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <Box className="text-box-card-white">
          <Box className="text-box-header">
            <h3>User Details</h3>
            {canEditUser && (
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() => navigate(`/users/edit/${userId}`)}
              >
                <IconBtnEditBase width={32} height={32} ariaLabel="Edit user" />
              </button>
            )}
          </Box>

          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">First Name</label>
              <TextField
                type="text"
                name="firstname"
                value={formData?.firstname}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Last Name</label>
              <TextField
                type="text"
                name="lastname"
                value={formData?.lastname}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Username</label>
              <TextField
                type="text"
                name="username"
                value={formData?.username}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Email</label>
              <TextField
                type="text"
                name="email"
                value={formData?.email}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Status</label>
              <TextField
                type="text"
                name="is_active"
                value={formData?.is_active ? "Active" : "Inactive"}
                className="form-control read-only"
                disabled
              />
            </Grid>
          </Grid>
        </Box>

        {/* Assignment Sections */}
        <Grid container rowSpacing={1.5} columnSpacing={1.5}>
          {/* Assigned Roles Section */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <AssignRole
              selectedRolesIds={[]}
              onRoleChange={() => {}}
              viewMode={true}
              assignedRoles={userAssignedRoles}
              label="Assigned Roles"
            />
          </Grid>

          {/* Assigned Tags Section */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <AssignTags
              selectedTags={[]}
              onTagChange={() => {}}
              viewMode={true}
              assignedTags={userAssignedAuthTags}
              label="Assigned Tags"
            />
          </Grid>

          {/* Assigned Systems Section */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <AssignSystem
              selectedSystems={[]}
              onSystemChange={() => {}}
              viewMode={true}
              assignedSystems={userAssignedSystems}
              label="Assigned Systems"
            />
          </Grid>

          {/* Assigned Groups Section */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <AssignGroup
              selectedGroupIds={[]}
              onGroupChange={() => {}}
              viewMode={true}
              assignedGroups={userAssignedGroups}
              label="Assigned Groups"
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ViewUser;
